import { getTranslation } from 'helpers/getTranslation';

export type AnalyticsChartType = 'funnel' | 'time_series' | 'retention';

export const DEFAULT_PRESET_ID = 'plugin-activation';

export const PRESET_CHART_TYPES: Record<string, AnalyticsChartType> = {
  'plugin-activation': 'funnel',
  'plugin-signup': 'funnel',
  'plugin-paywall': 'funnel',
  'plugin-lessons': 'funnel',
  'web-checkout': 'funnel',
  'web-otp-auth': 'funnel',
  payments: 'time_series',
  'backend-signups': 'time_series',
  'retention-signup': 'retention',
  'retention-first-task': 'retention',
};

export const COMBINED_PAYMENTS_PRESET_ID = 'payments';

export const PAYMENTS_SERIES = [
  {
    id: 'payment_succeeded',
    label: 'Payment Succeeded (Backend)',
    color: '#85E0AB',
    eventName: 'Payment Succeeded',
    platform: 'Backend',
  },
  {
    id: 'purchase_completed',
    label: 'Purchase Completed (Web)',
    color: '#5C738C',
    eventName: 'Purchase Completed',
    platform: 'Web',
  },
] as const;

export const buildTimeSeriesChartData = (
  points: Array<{ date: string; count: number }>,
  seriesId = 'events',
  color = '#85E0AB',
) => [
  {
    id: seriesId,
    color,
    data: points.map((point) => ({
      x: point.date,
      y: point.count,
    })),
  },
];

export const buildMultiTimeSeriesChartData = (
  series: Array<{
    id: string;
    label: string;
    color: string;
    data: Array<{ date: string; count: number }>;
  }>,
) =>
  series.map((item) => ({
    id: item.label,
    color: item.color,
    data: item.data.map((point) => ({
      x: point.date,
      y: point.count,
    })),
  }));

export const buildFunnelChartData = (
  steps: Array<{ step: string; count: number; conversionRate: number }>,
) =>
  steps.map((step) => ({
    step: step.step,
    count: step.count,
    conversionRate: step.conversionRate,
  }));

export const buildRetentionChartData = (
  points: Array<{ day: number; retained: number; rate: number }>,
) =>
  points.map((point) => ({
    day: `D${point.day}`,
    retained: point.retained,
    rate: point.rate,
  }));

export const getPeriodOptions = () => [
  { label: getTranslation('analytics_page.filters.period_7'), value: '7' },
  { label: getTranslation('analytics_page.filters.period_30'), value: '30' },
  { label: getTranslation('analytics_page.filters.period_90'), value: '90' },
];

export const getPlatformOptions = () => [
  { label: getTranslation('analytics_page.filters.all_platforms'), value: '' },
  { label: getTranslation('common.text.plugin'), value: 'Plugin' },
  { label: getTranslation('common.text.web'), value: 'Web' },
  { label: getTranslation('analytics_page.filters.backend'), value: 'Backend' },
];

export const getFunnelPresetOptions = () => [
  {
    label: getTranslation('analytics_page.filters.funnel_plugin_activation'),
    value: 'default',
  },
  {
    label: getTranslation('analytics_page.filters.funnel_plugin_signup'),
    value: 'plugin_signup',
  },
  {
    label: getTranslation('analytics_page.filters.funnel_plugin_paywall'),
    value: 'paywall',
  },
  {
    label: getTranslation('analytics_page.filters.funnel_lesson_engagement'),
    value: 'lesson',
  },
  {
    label: getTranslation('analytics_page.filters.funnel_web_checkout'),
    value: 'checkout',
  },
  {
    label: getTranslation('analytics_page.filters.funnel_web_otp_auth'),
    value: 'web_auth',
  },
];

export const getAllEventsOption = () => ({
  label: getTranslation('analytics_page.filters.all_events'),
  value: '',
});

export const PERIOD_OPTIONS = getPeriodOptions();

export const PLATFORM_OPTIONS = getPlatformOptions();

export const FUNNEL_PRESET_OPTIONS = getFunnelPresetOptions();

export const getDateRangeFromPeriod = (periodDays: string) => {
  const to = new Date();
  const from = new Date();

  from.setDate(from.getDate() - Number(periodDays || 30));

  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
};

export const getChartTypeForPreset = (presetId: string): AnalyticsChartType =>
  PRESET_CHART_TYPES[presetId] ?? 'funnel';

export const isCombinedPaymentsPreset = (presetId: string) =>
  presetId === COMBINED_PAYMENTS_PRESET_ID;

export const getDefaultFilterState = () => {
  const platformOptions = getPlatformOptions();
  const funnelPresetOptions = getFunnelPresetOptions();

  return {
    platform:
      platformOptions.find((option) => option.value === 'Plugin') ?? platformOptions[0],
    eventName: getAllEventsOption(),
    funnelPreset: funnelPresetOptions[0],
    retentionEventName: 'Signup Completed',
  };
};
